"use client";

import React, { useEffect } from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { MapPin, Globe2, Layers, ArrowRight, Check, Sparkles } from "lucide-react";

/** Market-test (market-test-plan.md §3): surfacing regional/global plans early
 *  lifts AOV vs a single-country default — the brand's signature upsell lever
 *  (research/07 CF3; required events regional_global_upsell_viewed/_selected,
 *  market-test §10). variant_id distinguishes the country/regional/global lane. */
const UPSELL_EXPERIMENT_ID = "pricing_premium_regional_global_v1";

const trackUpsellSelect = (tier: UpsellTier) => {
  try {
    track({
      event_name: "regional_global_upsell_selected",
      event_id: `regional_global_upsell_selected:${UPSELL_EXPERIMENT_ID}:${tier.kind}:${Date.now()}`,
      experiment_id: UPSELL_EXPERIMENT_ID,
      variant_id: tier.kind,
      items: [{ item_id: `${tier.kind}-${tier.dataLabel}`, item_name: `${tier.title} ${tier.dataLabel}`, region: tier.title }],
    });
  } catch {
    /* analytics is best-effort */
  }
};

export type UpsellTier = {
  /** Which lane this card represents (also the variant_id). */
  kind: "country" | "regional" | "global";
  /** Display name e.g. "Japan", "Asia", "Global". */
  title: string;
  /** e.g. "10GB" / "Unlimited" (label only — never a bare unlimited claim). */
  dataLabel: string;
  validityDays: number;
  /** Formatted retail price e.g. "$15.00" (real catalog price). */
  price?: string;
  /** Pre-built /checkout?... href OR /destinations/ (server keeps the contract). */
  href: string;
  recommended?: boolean;
};

/**
 * AquaGlobe — Regional & Global plan preview (DESIGN_BRIEF.md §4 homepage order:
 * sits between How-it-Works and the Compatibility preview). This is the brand's
 * DEFINING differentiator (research/07 CF3, content-pillars.md P2): it answers
 * "does it work across multiple countries?" by surfacing the country → regional
 * → global ladder with USE-CASE framing ("short trip" / "multi-country
 * favorite" / "frequent traveler" — strategy.md §7) and a compact visual
 * country-vs-regional-vs-global comparison. Real catalog data + checkout hrefs
 * are passed in from the server; this client component only handles measurement
 * and presentation. Glass-native (correct archetype). No price superlatives.
 */
export default function RegionalGlobalPreview({ tiers }: { tiers: UpsellTier[] }) {
  const { t } = useTranslations();

  useEffect(() => {
    try {
      track({
        event_name: "regional_global_upsell_viewed",
        event_id: `regional_global_upsell_viewed:${UPSELL_EXPERIMENT_ID}`,
        experiment_id: UPSELL_EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const onSelect = (tier: UpsellTier) => {
    trackUpsellSelect(tier);
  };

  const meta: Record<UpsellTier["kind"], { Icon: typeof MapPin; useCase: string; line: string }> = {
    country: {
      Icon: MapPin,
      useCase: t("aqua.upsell.countryUse", "Single destination"),
      line: t("aqua.upsell.countryLine", "One country, one simple plan — perfect for a focused city trip."),
    },
    regional: {
      Icon: Layers,
      useCase: t("aqua.upsell.regionalUse", "Multi-country favorite"),
      line: t("aqua.upsell.regionalLine", "Hop across a whole region on one eSIM — ideal for multi-stop trips."),
    },
    global: {
      Icon: Globe2,
      useCase: t("aqua.upsell.globalUse", "Frequent traveler"),
      line: t("aqua.upsell.globalLine", "Stay connected across continents — the choice for nomads and remote work."),
    },
  };

  // Compact "which fits you?" comparison rows (claim-safe, structural only).
  const compareRows: { label: string; country: string; regional: string; global: string }[] = [
    {
      label: t("aqua.upsell.cmpCoverage", "Coverage"),
      country: t("aqua.upsell.cmpCoverageCountry", "One country"),
      regional: t("aqua.upsell.cmpCoverageRegional", "A whole region"),
      global: t("aqua.upsell.cmpCoverageGlobal", "Worldwide"),
    },
    {
      label: t("aqua.upsell.cmpBestFor", "Best for"),
      country: t("aqua.upsell.cmpBestForCountry", "City breaks"),
      regional: t("aqua.upsell.cmpBestForRegional", "Multi-stop trips"),
      global: t("aqua.upsell.cmpBestForGlobal", "Always-on travel"),
    },
    {
      label: t("aqua.upsell.cmpSwitch", "Switching SIMs"),
      country: t("aqua.upsell.cmpSwitchCountry", "Per country"),
      regional: t("aqua.upsell.cmpSwitchRegional", "None in region"),
      global: t("aqua.upsell.cmpSwitchGlobal", "Never"),
    },
  ];

  return (
    <section className="container mx-auto px-4 py-12">
      <Reveal as="div" className="text-center max-w-2xl mx-auto mb-9" y={16}>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground inline-flex items-center gap-2">
          <Sparkles className="aqua-sparkle h-5 w-5" />
          {t("aqua.upsell.title", "Country, regional, or global?")}
        </h2>
        <p className="mt-3 text-muted-foreground">
          {t("aqua.upsell.sub", "Pick the coverage that fits your trip — go beyond a single country with a regional or global plan.")}
        </p>
      </Reveal>

      {/* Use-case framed tiers */}
      <Stagger className="grid md:grid-cols-3 gap-5 items-stretch" staggerChildren={0.09} amount={0.2}>
        {tiers.map((tier) => {
          const m = meta[tier.kind];
          const Icon = m.Icon;
          return (
            <StaggerItem
              as="div"
              y={16}
              key={tier.kind}
              className={`aqua-glass rounded-3xl p-6 backdrop-blur flex flex-col transition-transform hover:-translate-y-1 duration-300 ${
                tier.recommended ? "ring-2 ring-primary/50" : ""
              }`}
            >
              {tier.recommended && (
                <span className="self-start mb-3 inline-flex items-center gap-1 rounded-full aqua-gradient text-white text-[11px] font-bold uppercase tracking-wide px-3 py-1">
                  <Sparkles className="h-3 w-3" />
                  {t("aqua.upsell.recommended", "Recommended")}
                </span>
              )}
              <span className="aqua-icon inline-flex items-center justify-center rounded-2xl p-3 backdrop-blur self-start mb-4">
                <Icon className="h-6 w-6 text-primary" aria-hidden="true" />
              </span>
              <div className="text-xs font-semibold uppercase tracking-wide text-primary">{m.useCase}</div>
              <div className="mt-1 text-xl font-extrabold text-foreground">{tier.title}</div>
              <p className="mt-2 text-sm text-muted-foreground flex-1">{m.line}</p>

              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-lg font-bold text-foreground">{tier.dataLabel}</span>
                <span className="text-sm text-muted-foreground">
                  · {t("aqua.upsell.days", "{days} days").replace("{days}", String(tier.validityDays))}
                </span>
              </div>
              {tier.price && (
                <div className="mt-1 text-sm text-foreground">
                  <span className="text-muted-foreground">{t("aqua.upsell.from", "from")} </span>
                  <span className="font-bold">{tier.price}</span>
                </div>
              )}

              <Link
                href={tier.href}
                onClick={() => onSelect(tier)}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold h-11 shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all active:scale-[0.98]"
              >
                {t("aqua.upsell.choose", "Choose plan")} <ArrowRight className="h-4 w-4" />
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>

      {/* Compact country vs regional vs global comparison (signature differentiator) */}
      <Reveal as="div" className="aqua-glass rounded-3xl p-2 sm:p-4 mt-6 overflow-hidden" y={20}>
        <div className="grid grid-cols-4 gap-3 px-3 py-3 text-xs sm:text-sm font-bold text-foreground/70">
          <span />
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" />{t("aqua.upsell.colCountry", "Country")}</span>
          <span className="inline-flex items-center gap-1.5"><Layers className="h-4 w-4 text-primary" />{t("aqua.upsell.colRegional", "Regional")}</span>
          <span className="inline-flex items-center gap-1.5"><Globe2 className="h-4 w-4 text-primary" />{t("aqua.upsell.colGlobal", "Global")}</span>
        </div>
        <Stagger className="space-y-2" staggerChildren={0.06} amount={0.2}>
          {compareRows.map((row) => (
            <StaggerItem
              as="div"
              y={8}
              key={row.label}
              className="grid grid-cols-4 gap-3 items-center rounded-2xl bg-white/50 border border-white/60 backdrop-blur px-3 py-3 text-xs sm:text-sm"
            >
              <span className="font-semibold text-foreground/70">{row.label}</span>
              <span className="text-foreground">{row.country}</span>
              <span className="text-foreground">{row.regional}</span>
              <span className="inline-flex items-center gap-1.5 text-foreground font-medium">
                <Check className="h-3.5 w-3.5 text-success shrink-0" />{row.global}
              </span>
            </StaggerItem>
          ))}
        </Stagger>
      </Reveal>
    </section>
  );
}
